import { getLoggerSafe } from '@vitvio/logging';
import { DEFAULT_WORKDAY_START_HOUR, DEFAULT_WORKDAY_STOP_HOUR } from './constants';
import type { UserConfigurationState } from './state';
import { emptyConfiguration } from './state';

const USER_CONFIGURATION_KEY = 'user-configuration';

/**
 * Load the user configuration from the local storage. Currently we do not persist the user configuration to Backend.
 */
export function loadUserConfigurationLocal(orgID: string, userID: string): UserConfigurationState {
  const logger = getLoggerSafe();
  const userConfiguration = localStorage.getItem(`${USER_CONFIGURATION_KEY}-${userID}-${orgID}`);

  try {
    if (!userConfiguration) {
      return emptyConfiguration;
    }

    const configuration = JSON.parse(userConfiguration);

    if ('organizationID' in configuration === false) {
      logger?.error('Missing org ID in local config', { orgID, userID });
      return emptyConfiguration;
    }

    if (configuration.organizationID !== orgID) {
      logger?.error('Configuration does not match organization', {
        expected: orgID,
        actual: configuration.organizationID,
      });
    }

    if ('userID' in configuration === false) {
      logger?.error('Missing user ID in local configuration', { orgID, userID });
      return emptyConfiguration;
    }

    if (configuration.userID !== userID) {
      logger?.error('Configuration user ID does not match', { expected: userID, actual: configuration.userID });
    }

    return {
      organizationID: orgID,
      userID: userID,
      workday: {
        start: DEFAULT_WORKDAY_START_HOUR,
        stop: DEFAULT_WORKDAY_STOP_HOUR,
      },
      selectedHospitalID: configuration.selectedHospitalID,
      timeFormat: configuration?.timeFormat || '24hr',
    };
  } catch (error) {
    logger?.error(error as Error, { context: 'loadUserConfigurationLocal', orgID, userID });
    return emptyConfiguration;
  }
}

/**
 * Save the user configuration to the local storage. Currently we do not persist the user configuration to Backend.
 */
export function saveUserConfigurationLocal(configuration: UserConfigurationState) {
  localStorage.setItem(
    `${USER_CONFIGURATION_KEY}-${configuration.userID}-${configuration.organizationID}`,
    JSON.stringify(configuration),
  );
}
